/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import React from "react";
import MediaPrepHeaderSection from "./mediaPrepheaderSection";
import MediaPrepPageHeader from "./mediaPrepPageHeader";
import { useTranslation } from 'react-i18next';


interface Props {
  mediaList: any[];
  supplierList: any[];

  formData: {
    selectedMedia: string;
    supplierId: string;
    catalogNumber: string;
    initialPh: string;
    initialPhTolerance: string;
    finalPh: string;
    finalPhTolerance: string;
    referenceTemp: string;
    shelfLife: string;
  };

  errors: {  
    selectedMedia?: string;
    supplierId?: string;
    catalogNumber?: string;
    shelfLife?: string;
    initialPh?: string;
    finalPh?: string;
    referenceTemp?: string;
  };

  onChange: (field: string, value: any) => void;
  onMediaChange: (value: string) => void;

  onSave: () => void;
  onCancel: () => void;
  isSaving: boolean;
}

const MediaProductDefinitionForm: React.FC<Props> = ({
    mediaList,
    supplierList,
    formData,
    onChange,
    onMediaChange,
    onSave,
    onCancel,
    errors,
    isSaving,
}) => {
    const { t } = useTranslation();
    
    
    return (
        <div className="media-prep-scope">
        <MediaPrepHeaderSection />
        <MediaPrepPageHeader pageHeader={t('mediaPowderProductMaster.title')} />
        <div className="flex flex-col h-full bg-[#F3F6F9]">
        <div className="p-4 overflow-y-auto flex-1">
            <div className="px-1 pb-13 pt-0 max-w-full animate-in fade-in duration-500 min-h-full">
            {/* Main Form Container */}
            <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
                {/* Info Message */}
                <div className="px-5 py-3 border-b border-slate-50 flex items-center gap-2 text-slate-500">
                    <span className="material-symbols-outlined text-[14px] text-[#FF7043] bg-orange-50 p-[6px] rounded-md -mt-2">{t('mediaPowderProductMaster.icons.info')}</span>
                    <p className="text-xs font-medium italic tracking-tight">{t('mediaPowderProductMaster.form.infoText')}</p>
                </div>

                <div className="px-5 pt-2 pb-5 space-y-2">
                
                {/* Section 1: Media Selection */}
                <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-4 mb-1">
                    <div className="w-8 h-8 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center text-xs font-black -mt-1.5">1</div>
                    <h3 className="text-sm font-black text-slate-800 tracking-tight uppercase">{t('mediaPowderProductMaster.form.mediaSelection')}</h3>
                    </div>
                    <div className="pl-14 space-y-2">
                    <label className="text-[11px] font-black text-slate-500 uppercase tracking-widest block">{t('mediaPowderProductMaster.form.mediaName')} <span className="text-red-500">*</span></label>
                    <div className="relative group w-[48%]">
                        <select
                        value={formData.selectedMedia}
                        onChange={(e) => {
                        const value = e.target.value;
                        onMediaChange(value);
                        }}
                        className="w-full bg-slate-50 border border-transparent rounded-xl px-4 py-2 text-sm font-medium text-slate-600 appearance-none outline-none focus:bg-white focus:border-orange-500 transition-all cursor-pointer">
                        <option value="">{t('mediaPowderProductMaster.form.mediaPlaceholder')}</option>
                        {mediaList.map((item: any) => (
                            <option key={item.id} value={item.id}>{item.plate_type}</option>
                        ))}
                        </select>
                        {errors.selectedMedia && <p className="errorText">{errors.selectedMedia}</p>}
                        <span className="material-symbols-outlined absolute right-4 top-1/2 -translate-y-1/2 text-slate-300 pointer-events-none group-focus-within:text-orange-500">{t('mediaPowderProductMaster.icons.expand_more')}</span>
                    </div>
                    </div>
                </div>

                {/* Section 2: Supplier Details */}
                <div className="space-y-3">
                    <div className="flex items-center gap-4">
                    <div className="w-8 h-8 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center text-xs font-black -mt-1.5">2</div>
                    <h3 className="text-sm font-black text-slate-800 tracking-tight uppercase">{t('mediaPowderProductMaster.form.supplierDetails')}</h3>
                    </div>
                    <div className="pl-14 grid grid-cols-1 md:grid-cols-2 gap-10">
                    <div className="space-y-2">
                        <label className="text-[11px] font-black text-slate-500 uppercase tracking-widest block">{t('mediaPowderProductMaster.form.manufacturer')} <span className="text-red-500">*</span></label>
                        <div className="relative group">
                        <select
                            value={formData.supplierId}
                            onChange={(e) => {
                            const value = e.target.value;
                            const selected = supplierList.find(
                                (item: any) => item.id.toString() === value
                            );
                            onChange("supplierId", value);
                            onChange("supplierCode", selected?.code || "");
                            }}
                            className="w-full bg-slate-50 border border-transparent rounded-xl px-4 py-2 text-sm font-medium text-slate-600 appearance-none outline-none focus:bg-white focus:border-orange-500 transition-all cursor-pointer">
                        
                            <option value="">{t('mediaPowderProductMaster.form.manufacturerPlaceholder')}</option>
                            {supplierList.map((item: any) => (
                            <option key={item.id} value={item.id}>
                                {item.name}
                            </option>
                            ))}
                        </select>
                        {errors.supplierId && <p className="errorText">{errors.supplierId}</p>}
                        <span className="material-symbols-outlined absolute right-4 top-1/2 -translate-y-1/2 text-slate-300 pointer-events-none">{t('mediaPowderProductMaster.icons.expand_more')}</span>
                        </div>
                    </div>
                    <div className="space-y-2 -mt-1">
                        <label className="text-[11px] font-black text-slate-500 uppercase tracking-widest block">{t('mediaPowderProductMaster.form.catalogNumber')} <span className="text-red-500">*</span></label>
                        <input
                            value={formData.catalogNumber}
                            onChange={(e) => onChange("catalogNumber", e.target.value)}
                            type="text" 
                            placeholder={t('mediaPowderProductMaster.form.catalogPlaceholder')}
                            className="w-full bg-slate-50 border border-transparent rounded-xl px-4 py-2 text-sm font-medium text-slate-700 outline-none focus:bg-white focus:border-orange-500 transition-all"
                        />
                        <p className="text-[11.5px] text-slate-600 italic">{t('mediaPowderProductMaster.form.catalogDescription')}</p>
                        {errors.catalogNumber && <p className="errorText">{errors.catalogNumber}</p>}                    
                    </div>
                    </div>
                </div>

                {/* Section 3: Physico-chemical Specifications */}
                <div className="space-y-6">
                    <div className="flex items-center gap-4">
                    <div className="w-8 h-8 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center text-xs font-black -mt-1.5">3</div>
                    <h3 className="text-sm font-black text-slate-800 tracking-tight uppercase">{t('mediaPowderProductMaster.form.Physico-chemical Specifications')}</h3>
                    </div>
                    <div className="pl-14 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                        {/* Initial pH */}
                        <div className="space-y-2">
                        <label className="text-[11px] font-black text-slate-500 uppercase tracking-widest block">{t('mediaPowderProductMaster.form.initialPh')} <span className="text-red-500">*</span></label>
                        <div className="flex items-center gap-3">
                            <input 
                            value={formData.initialPh}
                            onChange={(e) => {
                            const value = e.target.value;

                            if (/^\d*\.?\d*$/.test(value)) {
                                onChange("initialPh", value);
                            }
                            }}
                            type="text" 
                            placeholder={t('mediaPowderProductMaster.form.targetPh')}
                            className="flex-1 bg-slate-50 border border-transparent rounded-xl px-4 py-2 text-sm font-medium text-slate-700 outline-none focus:bg-white focus:border-orange-500 transition-all"
                            />
                            
                            <div className="flex items-center gap-2 bg-slate-50 border border-transparent rounded-xl px-3 py-2">
                            <span className="text-slate-400 font-medium">±</span>
                            <input 
                                value={formData.initialPhTolerance}
                                onChange={(e) => onChange("initialPhTolerance", e.target.value)}
                                type="text" 
                                defaultValue="0.2" 
                                className="w-10 text-center text-sm font-black text-slate-600 outline-none bg-transparent border-none focus:outline-none focus:ring-0" />
                            </div>
                        </div>
                        {errors.initialPh && <p className="errorText">{errors.initialPh}</p>}
                        </div>
                        {/* Final pH */}
                        <div className="space-y-2">
                        <label className="text-[11px] font-black text-slate-500 uppercase tracking-widest block">{t('mediaPowderProductMaster.form.finalPh')} <span className="text-red-500">*</span></label>
                        <div className="flex items-center gap-3">
                            <input 
                            value={formData.finalPh}
                            onChange={(e) => {
                            const value = e.target.value;

                            if (/^\d*\.?\d*$/.test(value)) {
                                onChange("finalPh", value);
                            }
                            }}
                            type="text" 
                            placeholder={t('mediaPowderProductMaster.form.targetPh')} 
                            className="flex-1 bg-slate-50 border border-transparent rounded-xl px-4 py-2 text-sm font-medium text-slate-700 outline-none focus:bg-white focus:border-orange-500 transition-all"
                            />
                            <div className="flex items-center gap-2 bg-slate-50 border border-transparent rounded-xl px-3 py-2">
                            <span className="text-slate-400 font-medium">±</span>
                            <input 
                                value={formData.finalPhTolerance}
                                onChange={(e) => onChange("finalPhTolerance", e.target.value)}
                                type="text" 
                                defaultValue="0.2" 
                                className="w-10 text-center text-sm font-black text-slate-600 outline-none bg-transparent border-none focus:outline-none focus:ring-0" />
                            </div>
                        </div>
                        {errors.finalPh && <p className="errorText">{errors.finalPh}</p>}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                        <div className="space-y-2">
                        <label className="text-[11px] font-black text-slate-500 uppercase tracking-widest block">{t('mediaPowderProductMaster.form.referenceTemp')} <span className="text-red-500">*</span></label>
                        <div className="relative group">
                            <input 
                            value={formData.referenceTemp}
                            onChange={(e) => {
                                const value = e.target.value;
                                if (/^\d*\.?\d*$/.test(value)) {
                                    onChange("referenceTemp", value);
                                }
                            }}
                            type="text" 
                            placeholder={t('mediaPowderProductMaster.form.tempPlaceholder')}
                            className="w-full bg-slate-50 border border-transparent rounded-xl px-4 py-2 text-sm font-medium text-slate-700 outline-none focus:bg-white focus:border-orange-500 transition-all"
                            />
                            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[12px] font-medium text-slate-500 tracking-widest">°C</span>
                        {errors.referenceTemp && (<p className="errorText">{errors.referenceTemp}</p>)}
                        </div>
                        </div>
                        {/* <div className="space-y-2">
                        <label className="text-[11px] font-black text-slate-500 uppercase tracking-widest block">{t('mediaPowderProductMaster.form.sterilityRequirements')}</label>
                        <div className="relative group">
                            <select className="w-full bg-slate-50 border border-transparent rounded-xl px-4 py-2 text-sm font-medium text-slate-600 appearance-none outline-none focus:bg-white focus:border-orange-500 transition-all cursor-pointer">
                            <option value="">{t('mediaPowderProductMaster.form.sterilityPlaceholder')}</option>
                            <option value="sterile">Fully Sterile (Autoclaved)</option>
                            <option value="selective">Selective (Growth inhibition)</option>
                            </select>
                            <span className="material-symbols-outlined absolute right-4 top-1/2 -translate-y-1/2 text-slate-300 pointer-events-none">{t('mediaPowderProductMaster.icons.expand_more')}</span>
                        </div>
                        </div> */}

                        <div className="space-y-1 mt-1 -mb-9">
                        <label className="text-[11px] font-black text-slate-500 uppercase tracking-widest block">{t('mediaPowderProductMaster.form.shelfLife')} <span className="text-red-500">*</span></label>
                        <div className="relative group">
                            <input 
                            value={formData.shelfLife}
                            onChange={(e) => onChange("shelfLife", e.target.value)}
                            type="number" 
                            placeholder={t('mediaPowderProductMaster.form.shelfLifePlaceholder')} 
                            className="w-full bg-slate-50 border border-transparent rounded-xl px-4 py-2 text-sm font-medium text-slate-700 outline-none focus:bg-white focus:border-orange-500 transition-all"
                            />
                            {errors.shelfLife && <p className="errorText">{errors.shelfLife}</p>}
                            <span className="absolute right-16 top-1/2 -translate-y-1/2 text-[11px] font-black text-slate-500 uppercase tracking-widest">{t('mediaPowderProductMaster.form.Days')}</span>
                        </div>
                        <p className="text-[11.5px] text-slate-600 italic">{t('mediaPowderProductMaster.form.shelfLifeDescription')}</p>
                        </div>
                    </div>
                    </div>
                </div>
                </div>

                {/* Form Actions */}
                <div className="px-10 py-3 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
                <button 
                    onClick={onCancel}
                    className="px-10 py-3 bg-white border border-transparent rounded-xl text-[12px] font-black text-slate-500 uppercase tracking-widest hover:bg-slate-100 active:scale-95 transition-all shadow-sm"
                >
                    {t('mediaPowderProductMaster.form.Cancel')}
                </button>
                <button 
                    onClick={onSave}
                    disabled={isSaving}
                    className="px-10 py-3 bg-[#FF7043] text-white rounded-xl text-[12px] font-black border border-transparent uppercase tracking-widest hover:brightness-110 active:scale-95 transition-all shadow-lg shadow-orange-500/20"
                >
                    {t('mediaPowderProductMaster.form.Save')}
                </button>
                </div>
            </div>
            </div>
        </div>
        </div>
    </div>
    );
    };

    export default MediaProductDefinitionForm;
